import React from 'react';
import {
  Box,
  Container,
  Skeleton,
  alpha,
  useMediaQuery,
  useTheme,
} from '@mui/material';

const HomePageSkeleton = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));

  // Helper function to get responsive height
  const getResponsiveHeight = (desktop, tablet, mobile) => {
    if (isMobile) return mobile;
    if (isTablet) return tablet;
    return desktop;
  };

  return (
    <Box sx={{ bgcolor: '#FFFFFF', minHeight: '100vh', mt: { xs: 12.5, sm: 10, md: 10.5 } }}>
      
      {/* Hero Banner Skeleton */}
      <Box sx={{ mb: 4 }}>
        <Skeleton
          variant="rectangular"
          width="100%"
          height={getResponsiveHeight(500, 400, 300)}
          sx={{
            borderRadius: 0,
            bgcolor: alpha('#ec489a', 0.1),
          }}
          animation="wave"
        />
      </Box>

      {/* Stats Section Skeleton */}
      <Box sx={{ bgcolor: '#FFFFFF', py: { xs: 6, md: 8 }, borderBottom: `1px solid ${alpha('#ec489a', 0.1)}` }}>
        <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
          <Box sx={{ 
            display: 'flex', 
            flexWrap: 'wrap', 
            justifyContent: 'center', 
            gap: { xs: 3, sm: 4, md: 6 } 
          }}>
            {[1, 2, 3, 4].map((item) => (
              <Box key={item} sx={{ textAlign: 'center', flex: '1 1 140px' }}>
                <Skeleton
                  variant="circular"
                  width={getResponsiveHeight(64, 60, 56)}
                  height={getResponsiveHeight(64, 60, 56)}
                  sx={{ mx: 'auto', mb: 1.5 }}
                  animation="wave"
                />
                <Skeleton 
                  variant="text" 
                  width={getResponsiveHeight(100, 90, 80)} 
                  height={getResponsiveHeight(45, 40, 35)} 
                  sx={{ mx: 'auto', mb: 1 }}
                  animation="wave"
                />
                <Skeleton 
                  variant="text" 
                  width={getResponsiveHeight(120, 100, 90)} 
                  height={20} 
                  sx={{ mx: 'auto' }}
                  animation="wave"
                />
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      {/* Product Categories Section Skeleton */}
      <Box sx={{ py: { xs: 4, md: 6 }, bgcolor: '#FFFFFF' }}>
        <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
          {/* Section Header */}
          <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 5 } }}>
            <Skeleton 
              variant="text" 
              width={150} 
              height={30} 
              sx={{ mx: 'auto', mb: 1 }}
              animation="wave"
            />
            <Skeleton 
              variant="text" 
              width={getResponsiveHeight(400, 350, 280)} 
              height={getResponsiveHeight(55, 48, 40)} 
              sx={{ mx: 'auto', mb: 2 }}
              animation="wave"
            />
            <Skeleton 
              variant="text" 
              width={getResponsiveHeight(500, 450, 350)} 
              height={24} 
              sx={{ mx: 'auto' }}
              animation="wave"
            />
          </Box>

          {/* Categories Grid */}
          <Box sx={{ 
            display: 'grid',
            gridTemplateColumns: {
              xs: 'repeat(2, 1fr)',
              sm: 'repeat(3, 1fr)',
              md: 'repeat(4, 1fr)',
              lg: 'repeat(6, 1fr)'
            },
            gap: { xs: 2, sm: 3 }
          }}>
            {[1, 2, 3, 4, 5, 6].map((item) => (
              <Box key={item} sx={{ textAlign: 'center' }}>
                <Skeleton
                  variant="circular"
                  width={getResponsiveHeight(120, 100, 80)}
                  height={getResponsiveHeight(120, 100, 80)}
                  sx={{ mx: 'auto', mb: 1.5 }}
                  animation="wave"
                />
                <Skeleton 
                  variant="text" 
                  width={getResponsiveHeight(100, 80, 70)} 
                  height={25} 
                  sx={{ mx: 'auto', mb: 0.5 }}
                  animation="wave"
                />
                <Skeleton 
                  variant="text" 
                  width={60} 
                  height={20} 
                  sx={{ mx: 'auto' }}
                  animation="wave"
                />
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      {/* Collections Section Skeleton */}
      <Box sx={{ bgcolor: '#FFFFFF', py: { xs: 6, md: 10 } }}>
        <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
          {/* Section Header */}
          <Box sx={{ textAlign: 'center', mb: { xs: 5, md: 7 } }}>
            <Skeleton 
              variant="text" 
              width={120} 
              height={25} 
              sx={{ mx: 'auto', mb: 1 }}
              animation="wave"
            />
            <Skeleton 
              variant="text" 
              width={getResponsiveHeight(450, 380, 300)} 
              height={getResponsiveHeight(55, 48, 40)} 
              sx={{ mx: 'auto', mb: 2 }}
              animation="wave"
            />
            <Skeleton 
              variant="text" 
              width={getResponsiveHeight(550, 480, 380)} 
              height={24} 
              sx={{ mx: 'auto' }}
              animation="wave"
            />
          </Box>

          {/* Collections Grid */}
          <Box sx={{ 
            display: 'flex', 
            flexWrap: 'wrap', 
            gap: 3, 
            justifyContent: 'center',
            alignItems: 'stretch'
          }}>
            {[1, 2, 3].map((item) => (
              <Box
                key={item}
                sx={{ 
                  flex: '1 1 280px', 
                  minWidth: 260,
                  display: 'flex',
                }}
              >
                <Box sx={{
                  borderRadius: 4,
                  overflow: 'hidden',
                  width: '100%',
                  bgcolor: '#f5f5f5',
                }}>
                  <Skeleton
                    variant="rectangular"
                    width="100%"
                    height={getResponsiveHeight(240, 220, 200)}
                    animation="wave"
                  />
                  <Box sx={{ p: { xs: 2, sm: 3 } }}>
                    <Skeleton 
                      variant="text" 
                      width="80%" 
                      height={getResponsiveHeight(32, 28, 24)} 
                      sx={{ mb: 1 }}
                      animation="wave"
                    />
                    <Skeleton 
                      variant="text" 
                      width="100%" 
                      height={40} 
                      sx={{ mb: 2 }}
                      animation="wave"
                    />
                    <Skeleton 
                      variant="text" 
                      width={120} 
                      height={35} 
                      animation="wave"
                    />
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      {/* Footer Skeleton */}
      <Box sx={{ bgcolor: '#1a1a1a', color: 'white', py: { xs: 4, md: 6 } }}>
        <Container maxWidth="lg">
          <Box sx={{ 
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(4, 1fr)'
            },
            gap: 4
          }}>
            {[1, 2, 3, 4].map((item) => (
              <Box key={item}>
                <Skeleton 
                  variant="text" 
                  width={120} 
                  height={28} 
                  sx={{ mb: 2, bgcolor: alpha('#FFFFFF', 0.1) }}
                  animation="wave"
                />
                <Skeleton 
                  variant="text" 
                  width="100%" 
                  height={20} 
                  sx={{ mb: 1, bgcolor: alpha('#FFFFFF', 0.1) }}
                  animation="wave"
                />
                <Skeleton 
                  variant="text" 
                  width="90%" 
                  height={20} 
                  sx={{ mb: 1, bgcolor: alpha('#FFFFFF', 0.1) }}
                  animation="wave"
                />
                <Skeleton 
                  variant="text" 
                  width="80%" 
                  height={20} 
                  sx={{ bgcolor: alpha('#FFFFFF', 0.1) }}
                  animation="wave"
                />
              </Box>
            ))}
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default HomePageSkeleton;